import { Component } from '@theme/component';
import { CtaActionTakenEvent } from '@theme/events';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import {
  prefetchProductContent,
  getCachedContent,
  getOrFetchProductContent,
  showModalSkeleton,
  SDQuickAddComponent,
} from '@theme/sd-quick-add';

/**
 * SD CTA Quick View - Opens mini PDP modal or adds to cart for single-variant products
 *
 * This component delegates to the existing sd-quick-add-component when available,
 * or triggers the quick add modal directly when the quick add button is disabled.
 */
export class SDCtaQuickView extends Component {
  get isSingleVariant() {
    return this.hasAttribute('data-single-variant');
  }

  get useSmartBehavior() {
    return this.hasAttribute('data-smart-behavior');
  }

  /**
   * Gets the quick add component from the parent product card
   * @returns {HTMLElement & { handleClick?: (e: Event) => void, prefetch?: () => void } | null}
   */
  get quickAddComponent() {
    return this.closest('product-card')?.querySelector('sd-quick-add-component');
  }

  connectedCallback() {
    super.connectedCallback();
    const button = this.querySelector('button');
    button?.addEventListener('pointerenter', this.#handlePrefetch, { once: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const button = this.querySelector('button');
    button?.removeEventListener('pointerenter', this.#handlePrefetch);
  }

  /**
   * Prefetches the product page on hover via the shared cache utility.
   */
  #handlePrefetch = () => {
    const url = this.#getProductPageUrl();
    if (!url) return;
    prefetchProductContent(url);
  };

  /**
   * @returns {'mini-pdp' | 'navigate' | 'add-to-cart' | null} null when no experiment is active or value is unrecognized.
   */
  get assignedAction() {
    const raw = /** @type {HTMLElement | null} */ (this.closest('[data-cta-action]'))?.dataset.ctaAction;
    return raw === 'mini-pdp' || raw === 'navigate' || raw === 'add-to-cart' ? raw : null;
  }

  /**
   * Variant-C only fires for single-variant, in-stock products; everything else falls back to mini-pdp.
   * @param {'mini-pdp' | 'navigate' | 'add-to-cart'} assigned
   * @returns {'mini-pdp' | 'navigate' | 'add-to-cart'}
   */
  #resolveActualAction(assigned) {
    if (assigned === 'navigate') {
      return this.dataset.productUrl ? 'navigate' : 'mini-pdp';
    }
    if (assigned === 'add-to-cart') {
      const count = Number(this.dataset.productVariantCount);
      const available = this.dataset.productAvailable === 'true';
      const hasVariant = !!this.dataset.productDefaultVariantId;
      return count === 1 && available && hasVariant ? 'add-to-cart' : 'mini-pdp';
    }
    return 'mini-pdp';
  }

  /**
   * Dual emission: a typed event for in-theme listeners, plus a defensive dataLayer push (no-op when undefined).
   * @param {'mini-pdp' | 'navigate' | 'add-to-cart'} assigned
   * @param {'mini-pdp' | 'navigate' | 'add-to-cart'} actual
   */
  #emitActionTaken(assigned, actual) {
    const detail = {
      assigned,
      actual,
      productHandle: this.dataset.productHandle,
      variantId: this.dataset.productDefaultVariantId,
    };
    document.dispatchEvent(new CtaActionTakenEvent(detail));
    if (Array.isArray(/** @type {{ dataLayer?: unknown[] }} */ (window).dataLayer)) {
      /** @type {{ dataLayer: unknown[] }} */ (window).dataLayer.push({
        event: 'cta_action_taken',
        ...detail,
      });
    }
  }

  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const assigned = this.assignedAction;

    // Backward-compat: when the experiment attribute is absent, preserve
    // smart-ATC behavior for stores not yet enrolled in the test. No emit —
    // we only report on clicks that participated in the experiment.
    if (assigned === null) {
      if (this.useSmartBehavior && this.isSingleVariant) {
        await this.#addToCart(this.dataset.variantId);
        return;
      }
      const quickAdd = this.quickAddComponent;
      if (quickAdd?.handleClick) {
        quickAdd.dataset.productTitle = this.dataset.productTitle || '';
        quickAdd.handleClick(event);
        return;
      }
      await this.#openModalDirectly();
      return;
    }

    const actual = this.#resolveActualAction(assigned);

    // Emit before branching so navigate doesn't unload the page first.
    this.#emitActionTaken(assigned, actual);

    if (actual === 'navigate') {
      window.location.assign(this.dataset.productUrl ?? '');
      return;
    }
    if (actual === 'add-to-cart') {
      await this.#addToCart(this.dataset.productDefaultVariantId);
      return;
    }

    // mini-pdp — delegate to legacy quick-add component or fall back to direct modal.
    const quickAdd = this.quickAddComponent;
    if (quickAdd?.handleClick) {
      quickAdd.dataset.productTitle = this.dataset.productTitle || '';
      quickAdd.handleClick(event);
      return;
    }
    await this.#openModalDirectly();
  };

  /**
   * @param {string | undefined} variantId
   */
  async #addToCart(variantId) {
    if (!variantId) return;

    const button = this.querySelector('button');
    button?.classList.add('loading');

    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      const body = await response.json();

      if (response.ok) {
        // `/cart/add.js` answers with the added lines only; consumers want the cart.
        const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
          (cartResponse) => cartResponse.json()
        );

        deferredPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'sd-cta-quick-view', variantId, items: cart.items, didError: false },
        });
        button?.classList.add('atc-added');
        setTimeout(() => button?.classList.remove('atc-added'), 2000);
      } else {
        const message = body?.description || body?.message || `Add to cart failed (${response.status})`;
        deferredPromise.reject(new Error(message));
        this.dispatchEvent(
          new CartErrorEvent({ error: message, code: body?.status ? 'INVALID' : 'SERVICE_UNAVAILABLE' })
        );
        button?.classList.add('atc-error');
        setTimeout(() => button?.classList.remove('atc-error'), 2000);
      }
    } catch (error) {
      deferredPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      throw error;
    } finally {
      button?.classList.remove('loading');
    }
  }

  async #openModalDirectly() {
    const url = this.#getProductPageUrl();

    // The modal only renders when Mini PDP is enabled for at least one breakpoint
    // (layout/theme.liquid). With it disabled there is nothing to open, and
    // showDialog() would silently no-op, so send the shopper to the product page
    // rather than leaving a dead control.
    if (!document.getElementById('sd-quick-add-dialog')) {
      const productUrl = this.dataset.productUrl;
      if (productUrl) window.location.assign(productUrl);
      return;
    }

    // Temp component for updateQuickAddModal (needs product card context for mobile restructuring)
    const tempComponent = /** @type {InstanceType<typeof SDQuickAddComponent>} */ (
      document.createElement('sd-quick-add-component')
    );
    tempComponent.dataset.productTitle = this.dataset.productTitle || '';
    // Hosts outside a product-card have no link for the modal's title to read;
    // pass ours through so the mobile title link resolves (see productPageUrl).
    tempComponent.dataset.productUrl = this.dataset.productUrl || '';
    this.appendChild(tempComponent);

    try {
      let productGrid = getCachedContent(url);

      if (productGrid) {
        await tempComponent.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)));
        /** @type {HTMLElement & { showDialog?: () => void } | null} */
        const dialog = document.getElementById('sd-quick-add-dialog');
        dialog?.showDialog?.();
        return;
      }

      // Cache miss — show skeleton and open modal immediately
      showModalSkeleton(true);
      /** @type {HTMLElement & { showDialog?: () => void } | null} */
      const dialog = document.getElementById('sd-quick-add-dialog');
      dialog?.showDialog?.();

      productGrid = await getOrFetchProductContent(url);
      if (productGrid) {
        await tempComponent.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)));
      }

      showModalSkeleton(false);
    } finally {
      tempComponent.remove();
    }
  }

  #getProductPageUrl() {
    const productCard = this.closest('product-card');
    const productLink = productCard?.querySelector('a[href*="/products/"]');

    // Hosts outside a product-card (e.g. the cart drawer's You May Also Like
    // carousel) have no link to read, so fall back to the url we emit on the
    // element itself. data-product-url is root-relative, hence the base arg.
    const href = productLink?.href || this.dataset.productUrl;

    if (!href) return '';

    const url = new URL(href, window.location.origin);
    url.searchParams.set('view', 'sd-mini-pdp-DO-NOT-DELETE');
    return url.toString();
  }
}

if (!customElements.get('sd-cta-quick-view')) {
  customElements.define('sd-cta-quick-view', SDCtaQuickView);
}
